import { Tooltip } from 'react-tooltip';
import { Common } from '@thecvlb/design-system';
import { useFlag } from '@unleash/proxy-client-react';
import classNames from 'classnames';

import InterpreterButton from 'widgets/zoom/InterpreterButton';

import { useMessages, useZoomCall } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';
import { useWidth } from 'hooks/useWidth';
import { FeatureFlag } from 'utils/enums';

import { useSettingsHook } from './settings.hook';
import { getDeviceIcon, pointClassName, type SettingsDeviceType } from './settings.settings';

// --- Subcomponents ---

type DeviceButtonProps = {
  device: MediaDeviceInfo;
  onSelect: (deviceId: string, type: SettingsDeviceType) => void;
  selectedId: string;
  type: SettingsDeviceType;
};

const DeviceButton = ({ device, onSelect, selectedId, type }: DeviceButtonProps) => (
  <button
    type="button"
    className={classNames('flex items-center w-full gap-2 py-2 text-sm', {
      'font-bold': selectedId === device.deviceId
    })}
    onClick={() => onSelect(device.deviceId, type)}
  >
    <Common.Icon name={getDeviceIcon(device, type)} />
    <span className="flex-1 text-start">{device.label}</span>
    <Common.Icon
      className={classNames('text-green', { 'opacity-0': selectedId !== device.deviceId })}
      name="check-circle"
    />
  </button>
);

const VolumeMeter = ({ volume }: { volume: number }) => (
  <div className="flex w-8 justify-end text-center">
    <span className={pointClassName(volume, 2.5)}>●</span>
    <span className={pointClassName(volume, 5)}>●</span>
    <span className={pointClassName(volume, 7.5)}>●</span>
  </div>
);

type DeviceSectionProps = {
  devices: MediaDeviceInfo[];
  extra?: React.ReactNode;
  onSelect: (deviceId: string, type: SettingsDeviceType) => void;
  selectedId: string;
  title: string;
  type: SettingsDeviceType;
};

const DeviceSection = ({
  devices,
  extra,
  onSelect,
  selectedId,
  title,
  type
}: DeviceSectionProps) => (
  <>
    <div className="mt-4 flex items-center gap-1 text-base">
      <span className="flex-1 text-sm text-gray">{title}</span>
      {extra}
    </div>
    {devices.map((device) => (
      <DeviceButton
        key={device.deviceId}
        device={device}
        selectedId={selectedId}
        type={type}
        onSelect={onSelect}
      />
    ))}
    <hr className="mt-4" />
  </>
);

// --- Main component ---

const Settings = () => {
  const { isMobile } = useWidth();
  const logEvent = useAnalytics();
  const { channelDetails, channels } = useMessages();
  const {
    callOutParticipant,
    cameraId,
    isEnableCaptions,
    isOpenSettings,
    languageRequested,
    microphoneId,
    room,
    showControls,
    speakerId,
    toggleIsEnableCaptions,
    toggleIsOpenSettings
  } = useZoomCall();
  const {
    audioInputsDevices,
    audioOutputsDevices,
    disabledAudioButton,
    handleClickRequestInterpreter,
    handlePlayAudio,
    switchDevice,
    videoDevices,
    volume
  } = useSettingsHook();
  const interpreterOnCallEnabled = useFlag(FeatureFlag.InterpreterOnCall);
  const isAllowedToSelectDevices = useFlag(FeatureFlag.ZoomAllowedToSelectDevices);
  const showDeviceSections = isAllowedToSelectDevices || !isMobile;

  const handleToggleCaptions = () => {
    logEvent('call_settings_captions_switch_click', {
      enabled: !isEnableCaptions
    });
    toggleIsEnableCaptions();
  };

  const channel = channels.find((item) => item.channelId === channelDetails?._id);

  const content = (
    <>
      <DeviceSection
        title="Microphone"
        devices={showDeviceSections ? audioInputsDevices : []}
        selectedId={microphoneId}
        type="audioInput"
        onSelect={(deviceId, type) => switchDevice(deviceId, type, true)}
        extra={<VolumeMeter volume={volume} />}
      />
      <DeviceSection
        title="Speaker"
        devices={showDeviceSections ? audioOutputsDevices : []}
        selectedId={speakerId}
        type="audioOutput"
        onSelect={(deviceId, type) => switchDevice(deviceId, type, true)}
        extra={
          <Common.Button
            color="white-alt"
            size="sm"
            disabled={disabledAudioButton}
            onClick={handlePlayAudio}
          >
            Play test sound
          </Common.Button>
        }
      />
      {showDeviceSections && (
        <DeviceSection
          title="Camera"
          devices={videoDevices}
          selectedId={cameraId}
          type="video"
          onSelect={(deviceId, type) => switchDevice(deviceId, type, true)}
        />
      )}
      <span className="mt-4 text-sm text-gray"></span>
      <p className="mt-4 text-sm text-gray">Accessibility</p>
      <label className="flex cursor-pointer items-center gap-2 my-0.5">
        <Common.Icon name="caption-filled" />
        <span className="flex-1 text-sm">Captions</span>
        <Common.Toggle checked={isEnableCaptions} color="blue" onClick={handleToggleCaptions} />
      </label>
      {interpreterOnCallEnabled && room && (
        <InterpreterButton
          isRequested={!!(languageRequested || callOutParticipant)}
          className="!px-0 gap-2 !text-sm font-medium w-full"
          size="lg"
          onClick={handleClickRequestInterpreter}
        />
      )}
    </>
  );

  if (isMobile) {
    return (
      <Common.Modal close={toggleIsOpenSettings} isOpen={isOpenSettings} size="sm">
        {content}
        <Common.Button
          className="mt-4"
          color="white-alt"
          fullWidthOnMobile
          onClick={toggleIsOpenSettings}
        >
          Close
        </Common.Button>
      </Common.Modal>
    );
  }

  return (
    <Tooltip
      children={content}
      className="z-50 flex max-w-[300px] flex-col !rounded-xl !p-4 !text-sm !shadow"
      hidden={!!room && !showControls && !channel?.unreadMessageCount}
      id="settings-tooltip"
      opacity={1}
      variant="light"
      clickable
      noArrow
      openOnClick
    />
  );
};

export default Settings;
